/**
 * External Link Preferences Service
 * Manages user preferences for external link warnings using localStorage
 */

const STORAGE_KEY = 'burnt_external_link_preferences';

/**
 * Default preferences structure
 */
const DEFAULT_PREFERENCES = {
  warningsDisabled: false,
  timestamp: Date.now()
};

/**
 * Validates preference data structure
 * @param {any} data - Data to validate
 * @returns {boolean} True if valid
 */
const isValidPreferences = (data) => {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.warningsDisabled === 'boolean' &&
    typeof data.timestamp === 'number' &&
    data.timestamp > 0
  );
};

/**
 * Gets user preferences from localStorage
 * @returns {Object} User preferences object
 */
export const getExternalLinkPreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (!stored) {
      return { ...DEFAULT_PREFERENCES };
    }

    const parsed = JSON.parse(stored);
    
    if (!isValidPreferences(parsed)) {
      console.warn('Invalid external link preferences found, resetting to defaults');
      // Reset to defaults without calling setExternalLinkPreferences to avoid recursion
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_PREFERENCES));
      } catch (resetError) {
        console.error('Failed to reset preferences:', resetError);
      }
      return { ...DEFAULT_PREFERENCES };
    }

    return parsed;
  } catch (error) {
    console.error('Error reading external link preferences:', error);
    // Return defaults if localStorage fails
    return { ...DEFAULT_PREFERENCES };
  }
};

/**
 * Sets user preferences in localStorage
 * @param {Object} preferences - Preferences object to store
 * @returns {boolean} True if successful
 */
export const setExternalLinkPreferences = (preferences) => {
  try {
    if (!isValidPreferences(preferences)) {
      console.error('Invalid preferences object provided');
      return false;
    }

    const dataToStore = {
      ...preferences,
      timestamp: Date.now() // Always update timestamp when saving
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    return true;
  } catch (error) {
    console.error('Error saving external link preferences:', error);
    
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, attempting to clear old data');
      try {
        // Try to clear just our key and retry
        localStorage.removeItem(STORAGE_KEY);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
        return true;
      } catch (retryError) {
        console.error('Failed to save preferences after clearing:', retryError);
        return false;
      }
    }
    
    return false;
  }
};

/**
 * Updates specific preference fields
 * @param {Object} updates - Partial preferences object with updates
 * @returns {boolean} True if successful
 */
export const updateExternalLinkPreferences = (updates) => {
  try {
    const currentPreferences = getExternalLinkPreferences();
    const updatedPreferences = {
      ...currentPreferences,
      ...updates,
      timestamp: Date.now()
    };

    return setExternalLinkPreferences(updatedPreferences);
  } catch (error) {
    console.error('Error updating external link preferences:', error);
    return false;
  }
};

/**
 * Disables external link warnings
 * @returns {boolean} True if successful
 */
export const disableExternalLinkWarnings = () => {
  return updateExternalLinkPreferences({ warningsDisabled: true });
};

/**
 * Enables external link warnings
 * @returns {boolean} True if successful
 */
export const enableExternalLinkWarnings = () => {
  return updateExternalLinkPreferences({ warningsDisabled: false });
};

/**
 * Resets preferences to defaults
 * @returns {boolean} True if successful
 */
export const resetExternalLinkPreferences = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error resetting external link preferences:', error);
    return false;
  }
};

/**
 * Checks if external link warnings are disabled
 * @returns {boolean} True if warnings are disabled
 */
export const areExternalLinkWarningsDisabled = () => {
  const preferences = getExternalLinkPreferences();
  return preferences.warningsDisabled;
};

/**
 * Gets the timestamp when preferences were last updated
 * @returns {number} Timestamp in milliseconds
 */
export const getPreferencesTimestamp = () => {
  const preferences = getExternalLinkPreferences();
  return preferences.timestamp;
};

/**
 * Checks if localStorage is available
 * @returns {boolean} True if localStorage is available
 */
export const isStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Migrates old preference formats if needed
 * This function can be used to handle preference format changes in the future
 */
export const migratePreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (!stored) {
      return; // No preferences to migrate
    }

    const parsed = JSON.parse(stored);
    
    // Check if this is an old format that needs migration
    // For now, we just validate and reset if invalid
    if (!isValidPreferences(parsed)) {
      console.log('Migrating old external link preferences format');
      resetExternalLinkPreferences();
    }
  } catch (error) {
    console.error('Error during preference migration:', error);
    // Reset preferences if migration fails
    resetExternalLinkPreferences();
  }
};